import { emptyFragment } from '@execonline-inc/execonline-ui';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { just } from 'maybeasy';
import DashboardSummary from '../../../../components/DashboardSummary';
import {
  getDefaultDateRange,
  saveDateFilter,
  loadDateFilter,
} from '../../../../components/DashboardSummary/utils';
import DashboardSummaryStore from '../../../../DashboardSummaryStore';
import { DashboardSummaryPayload } from '../../../../DashboardSummaryStore/Types';
import { currentUserStore } from '../../../../CurrentUserStore';
import { findLinkBy } from '../../../../Links';
import { generateQuickSightSummary } from '../../../../services/quicksightSummaryService';
import { Link } from '../../../../Resource/Types';

function ImpactSummary() {
  // Create store instance per component to avoid stale state across remounts
  const [summaryStore] = useState(() => new DashboardSummaryStore());
  const summaryLink = findLinkBy({ rel: 'impact-report-summaries' }, currentUserStore.links);
  const actionPlanReportLink = findLinkBy({ rel: 'action-plan-report' }, currentUserStore.links);
  const linkRef = useRef<Link | null>(null);
  const hasLoadedRef = useRef<boolean>(false);

  // Store the link for use in callbacks
  summaryLink.map((link) => {
    linkRef.current = link;
  });

  const loadSummary = useCallback(
    (link: Link, customStartDate?: string, customEndDate?: string) => {
      // Use default dates from utility function
      const defaults = getDefaultDateRange();
      const startDate = customStartDate || defaults.startDate;
      const endDate = customEndDate || defaults.endDate;

      const dashboardId = 'home-page-impact';

      const request = {
        dashboardId,
        filters: {
          date_type: 'registration',
          start_date: startDate,
          end_date: endDate,
        },
      };

      summaryStore.startLoading(dashboardId);
      generateQuickSightSummary(link, request).fork(
        (error) => {
          console.error('Failed to generate home page summary:', error);
          summaryStore.setFailed('Failed to generate summary');
        },
        (response) => {
          // Handle empty data case
          if (response.status === 'empty') {
            const message = response.message.getOrElse(
              () =>
                "No action plan data is available for analysis yet. Once participants complete action plans, you'll see an AI-powered summary here.",
            );
            summaryStore.setEmpty(message, {
              startDate: just(startDate),
              endDate: just(endDate),
            });
            return;
          }

          // Transform response to match DashboardSummaryPayload
          const payload: DashboardSummaryPayload = {
            summary: response.summary,
            headline: response.headline,
            rationale: just(response.rationale),
            fullAnalysis: response.fullAnalysis,
            metadata: {
              dashboardId: response.metadata.map((m) => m.dashboardId).getOrElse(() => dashboardId),
              tabId: response.metadata.andThen((m) => m.tabId),
              generatedAt: response.metadata
                .map((m) => m.generatedAt)
                .getOrElse(() => new Date().toISOString()),
            },
            filters: {
              startDate: just(startDate),
              endDate: just(endDate),
            },
          };
          summaryStore.setSuccess(payload);
        },
      );
    },
    [summaryStore],
  );

  useEffect(() => {
    // Auto-load summary on mount with saved or default filters (only once)
    if (hasLoadedRef.current) return;

    summaryLink.map((link) => {
      hasLoadedRef.current = true;
      // Try to load saved date filter, fall back to defaults
      const savedFilter = loadDateFilter();
      if (savedFilter) {
        loadSummary(link, savedFilter.startDate, savedFilter.endDate);
      } else {
        // No saved filter - use defaults and save them so Reports page can pick them up
        const defaults = getDefaultDateRange();
        saveDateFilter(defaults.startDate, defaults.endDate);
        loadSummary(link);
      }
    });
  }, [summaryLink, loadSummary]);

  const handleRefresh = useCallback(() => {
    if (linkRef.current) {
      loadSummary(linkRef.current);
    }
  }, [loadSummary]);

  const handleDateRangeChange = useCallback(
    (startDate: string, endDate: string) => {
      // Save filter to localStorage so Reports page can pick it up
      saveDateFilter(startDate, endDate);
      if (linkRef.current) {
        loadSummary(linkRef.current, startDate, endDate);
      }
    },
    [loadSummary],
  );

  // Only render if the link exists
  return summaryLink
    .map(() => (
      <div className="mt-7">
        <DashboardSummary
          store={summaryStore}
          onRefresh={handleRefresh}
          onDateRangeChange={handleDateRangeChange}
          fullReportLink={actionPlanReportLink}
        />
      </div>
    ))
    .getOrElse(emptyFragment);
}

export default observer(ImpactSummary);
